/**
 * DeliveryGroupFeed — the farmer-facing feed rows (#1289 C5, redesigned
 * for #1311 reqs 4–9).
 *
 * One EntityTile (design-system row primitive — same anatomy as /farm
 * and the /documents look) per match group, rendered as a STATIC row
 * (``expandable={false}``): no chevron, no button semantics, no
 * accordion body. Each side carries its lucide icon (Truck for LS,
 * Receipt for GS) OUTSIDE the badge balloons, then ONE single-style
 * balloon PER DOCUMENT (#1400 — ``LS#<nr> <dd.mm.yyyy>`` / ``GS#<nr>
 * <dd.mm.yyyy>``; multi-document groups render one badge per event
 * instead of a folded ``+N`` summary); the GS money is its own
 * ``.dg-row__amount`` badge after the GS balloons, and an
 * orphaned LS with a fuzzy-priced estimate gets a ``ca. …€``
 * ``.dg-row__amount--estimate`` badge after the LS balloon. Missing
 * sides render a dashed badge carrying the same LS Truck / GS Receipt
 * icon FeedSide uses before the text (#1402) — amber "Lieferschein
 * fehlt" / red "Gutschrift fehlt", the severity mapping reconciled with
 * #1285's dashboard tiles.
 *
 * Pagination contract (single owner): the PAGE owns the page state and
 * hands over the FULL sorted+filtered list; THIS component owns the
 * slice and renders the shared Paginator with totalItems = full list
 * length. /documents paginates server-side, but useMatchGroups already
 * loads the whole month, so the slice is client-side by necessity.
 */
import { useMemo } from 'react';
import { GitCompareArrows, Receipt, Truck } from 'lucide-react';
import { EntityTile, Paginator, type EntityIconVariant } from '@hofsync/design-system';
import type { MatchGroup } from '../../../hooks/useMatchGroups';
import { DEFAULT_PAGE_SIZE } from '../../../hooks/useDocuments/useDocuments';
import {
  deliveryGroupSummary,
  deliveryGroupBadges,
  eurFmt,
  type DeliveryGroupSideSummary,
  type DeliveryGroupDocBadge,
} from './deliveryGroupSummary';
import { buildPriceIndex, estimateOrphanDeliveryValue } from './priceIndex';
import { groupHealthChips } from './groupHealth';
import './DeliveryGroupFeed.css';

interface DeliveryGroupFeedProps {
  /** FULL sorted + filtered list — the component slices, the page never does. */
  groups: MatchGroup[];
  /** 0-indexed page owned by the page (reset on month/status change). */
  currentPage: number;
  onPageChange: (page: number) => void;
  /**
   * Unfiltered month set for the orphan-LS price index. Mirrors
   * MatchGraph's ``allGroups`` (#1289 / #1289 canvas precedent,
   * ``GutschriftCheckPage.tsx``): estimates must not flap when the user
   * filters the feed down to a single status, so the price evidence is
   * always seeded from the full month rather than the visible slice.
   * Falls back to ``groups`` when absent.
   */
  allGroups?: MatchGroup[];
  /**
   * #1397 — invoked with a badge's source ``entityId`` when the operator
   * clicks a per-document LS/GS balloon. The page wires this to
   * ``setSelectedDocId`` so the lifted ``DocumentPreviewPanel`` opens the
   * matching document. When absent, badges render as plain
   * (non-interactive) spans — the canvas/tests that mount the feed
   * without a preview holder keep their static-row vocabulary.
   */
  onBadgeClick?: (entityId: string) => void;
  /**
   * #1397 — the currently previewed document id (page state). The
   * balloon for this id renders with the ``--active`` affordance +
   * ``aria-pressed`` so the operator sees which document the panel shows.
   */
  selectedDocId?: string | null;
}

const ICON_VARIANT_BY_STATUS: Record<MatchGroup['status'], EntityIconVariant> = {
  matched: 'success',
  partial: 'warning',
  orphan_settlement: 'warning',
  orphan_delivery: 'urgent',
};

/** Row title: the LS party, else the GS party (MatchGraph letterhead
 *  semantics — GS ``buyer_name`` is the issuer). */
function groupTitle(group: MatchGroup): string {
  const de = group.deliveryEvent;
  if (de) return String(de.sender_name ?? de.name ?? '—');
  const se = group.settlementEvent;
  if (se) return String(se.buyer_name ?? se.name ?? '—');
  return '—';
}

/**
 * One LS/GS side: a single icon OUTSIDE the balloons (req 5), then ONE
 * single-style ``<prefix>#<nr> <date>`` balloon PER DOCUMENT (#1400 —
 * the ``+N`` folding is retired; ``deliveryGroupBadges`` expands the
 * side into one descriptor per event), then — GS only — a separate money
 * badge (req 7), or — LS only — a ``ca. …€`` estimate badge (req 8).
 *
 * Money/estimate are rendered ONCE for the side, not per document:
 * ``group.money`` is the engine's COMPONENT total for the whole side and
 * isn't attributable to a single document. Each balloon carries its
 * source ``data-entity-id`` so #1397 can open the matching preview.
 *
 * #1397 — when ``onBadgeClick`` is supplied AND a badge carries a
 * ``docId`` (the event's ``source_doc_id``, i.e. a real preview target),
 * that balloon renders as a real ``<button>`` so the operator can click
 * (or keyboard-activate) it to open the document in the lifted
 * ``DocumentPreviewPanel``. Badges without a ``docId`` (no source
 * document) and feeds mounted without a preview holder keep the plain
 * ``<span>`` so the static-row vocabulary is unchanged there — mirroring
 * the canvas's ``source_doc_id ?? null`` guard.
 */
function FeedSide({
  side,
  badges,
  summary,
  estimate,
  onBadgeClick,
  selectedDocId,
}: {
  side: 'ls' | 'gs';
  badges: DeliveryGroupDocBadge[];
  summary: DeliveryGroupSideSummary;
  estimate?: number | null;
  onBadgeClick?: (entityId: string) => void;
  selectedDocId?: string | null;
}) {
  const Icon = side === 'ls' ? Truck : Receipt;
  const prefix = side === 'ls' ? 'LS' : 'GS';
  const showMoney = side === 'gs' && summary.money != null && summary.money > 0;
  const showEstimate = side === 'ls' && estimate != null && estimate > 0;
  return (
    <span className={`dg-row__side dg-row__side--${side}`}>
      <Icon size={14} aria-hidden="true" />
      {badges.map((badge, i) => {
        const label = `${prefix}#${badge.number} ${badge.dateFormatted}`;
        const key = badge.entityId ?? badge.docId ?? `${side}-${i}`;
        const clickable = onBadgeClick != null && badge.docId != null;
        // #1396 — user-facing flag marker. Shown right after the balloon
        // when the document is flagged; the reviewer's note is the tooltip
        // (generic "Markiert" for a bare flag) so the farmer sees *why* the
        // document was singled out. NON-destructive — the balloon itself is
        // unchanged. Rendered as a sibling of the balloon (not inside the
        // clickable <button>) so it never becomes part of the preview hit
        // target.
        const flag = badge.isFlagged ? (
          <span
            key={`${key}-flag`}
            className="dg-row__flag"
            title={badge.flagComment ?? 'Markiert'}
            aria-label={badge.flagComment ? `Markiert: ${badge.flagComment}` : 'Markiert'}
          >
            ⚑
          </span>
        ) : null;
        if (clickable) {
          const isActive = selectedDocId != null && selectedDocId === badge.docId;
          return (
            <span key={key} className="dg-row__balloon-wrap">
              <button
                type="button"
                className={`dg-row__balloon dg-row__balloon--${side} dg-row__balloon--clickable${
                  isActive ? ' dg-row__balloon--active' : ''
                }`}
                data-entity-id={badge.entityId}
                aria-pressed={isActive}
                onClick={() => onBadgeClick(badge.docId!)}
              >
                {label}
              </button>
              {flag}
            </span>
          );
        }
        return (
          <span key={key} className="dg-row__balloon-wrap">
            <span
              className={`dg-row__balloon dg-row__balloon--${side}`}
              data-entity-id={badge.entityId}
            >
              {label}
            </span>
            {flag}
          </span>
        );
      })}
      {showMoney && <span className="dg-row__amount">{eurFmt.format(summary.money!)}</span>}
      {showEstimate && (
        <span className="dg-row__amount dg-row__amount--estimate">
          {`ca. ${eurFmt.format(estimate!)}`}
        </span>
      )}
    </span>
  );
}

function MissingSide({ side }: { side: 'ls' | 'gs' }) {
  // #1402: carry the same LS Truck / GS Receipt icon FeedSide uses, before
  // the "fehlt" text, so the missing slot reads with the same vocabulary.
  const Icon = side === 'ls' ? Truck : Receipt;
  return (
    <span className={`dg-row__missing dg-row__missing--${side}`}>
      <Icon size={14} aria-hidden="true" />
      {side === 'ls' ? 'Lieferschein fehlt' : 'Gutschrift fehlt'}
    </span>
  );
}

/**
 * HOF-215 — the group's own health, as chips. Renders nothing at all for a
 * healthy group, so the feed's density is unchanged for the population this
 * change is not about.
 */
export function GroupHealth({ group }: { group: MatchGroup }) {
  const chips = groupHealthChips(group);
  if (chips.length === 0) return null;
  return (
    <span className="dg-row__health" data-testid="group-health">
      {chips.map((chip) => (
        <span
          key={chip.id}
          className={`dg-row__health-chip dg-row__health-chip--${chip.tone}`}
          title={chip.title}
          data-reason={chip.id}
        >
          {chip.label}
        </span>
      ))}
    </span>
  );
}

export default function DeliveryGroupFeed({
  groups,
  currentPage,
  onPageChange,
  allGroups,
  onBadgeClick,
  selectedDocId,
}: DeliveryGroupFeedProps) {
  // Orphan-LS price index — seeded from the UNFILTERED month set so the
  // estimate doesn't flap when a tile filter narrows the visible feed
  // (mirrors the canvas's ``allGroups`` threading; raw resolved-inclusive
  // groups are deliberate, resolved pairs remain valid price evidence).
  const priceIndex = useMemo(() => buildPriceIndex(allGroups ?? groups), [allGroups, groups]);

  // Codex round-3 P2 — clamp: a background refetch can shrink the list
  // while the page state still points past the end (another user
  // resolves rows, rematerialisation collapses groups); an unclamped
  // slice would strand the user on a blank feed with the paginator
  // hidden. Clamping is slice-domain, so it lives here with the slice.
  const lastPage = Math.max(0, Math.ceil(groups.length / DEFAULT_PAGE_SIZE) - 1);
  const safePage = Math.min(currentPage, lastPage);

  const pageGroups = groups.slice(
    safePage * DEFAULT_PAGE_SIZE,
    (safePage + 1) * DEFAULT_PAGE_SIZE,
  );

  return (
    <div className="delivery-group-feed">
      <div className="delivery-group-feed__rows">
        {pageGroups.map((group, index) => {
          const { ls, gs } = deliveryGroupSummary(group);
          const badges = deliveryGroupBadges(group);
          const estimate =
            group.status === 'orphan_delivery'
              ? estimateOrphanDeliveryValue(group, priceIndex)
              : null;
          return (
            <EntityTile
              key={group.id}
              icon={<GitCompareArrows size={20} />}
              iconVariant={ICON_VARIANT_BY_STATUS[group.status]}
              title={groupTitle(group)}
              tags={
                <span className="dg-row__sides">
                  {ls ? (
                    <FeedSide
                      side="ls"
                      badges={badges.ls}
                      summary={ls}
                      estimate={estimate}
                      onBadgeClick={onBadgeClick}
                      selectedDocId={selectedDocId}
                    />
                  ) : (
                    <MissingSide side="ls" />
                  )}
                  {gs ? (
                    <FeedSide
                      side="gs"
                      badges={badges.gs}
                      summary={gs}
                      onBadgeClick={onBadgeClick}
                      selectedDocId={selectedDocId}
                    />
                  ) : (
                    <MissingSide side="gs" />
                  )}
                  {/* HOF-215 AC 6 + AC 9 — the degraded-close vocabulary has
                      been on the fact since HOF-90 and was rendered nowhere:
                      38 Wiesenhof groups were known-degraded, counted, and
                      invisible to whoever reconciles the month. Silent on a
                      healthy group. */}
                  <GroupHealth group={group} />
                </span>
              }
              expandable={false}
              isEvenRow={index % 2 === 0}
            />
          );
        })}
      </div>
      <Paginator
        currentPage={safePage}
        pageSize={DEFAULT_PAGE_SIZE}
        totalItems={groups.length}
        onPageChange={onPageChange}
        className="delivery-group-feed__paginator"
      />
    </div>
  );
}
